import { ApplePay, GooglePay, SubmitActions, UIElement } from '@adyen/adyen-web';
import { Alert, Col, Flex, H4, ModalBody, Row } from '@appliedsystems/applied-design-system';
import { AdyenPaymentMethod, PaymentMethod, TransactionType } from '@appliedsystems/payments-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdyenConfig } from 'src/hooks/useAdyenConfiguration';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';
import { useMakePayment } from 'src/hooks/useMakePayment';
import { useAgencyDetailsStore } from 'src/store/AgencyDetail';
import { Locale } from 'src/store/Locale';
import { usePaymentMethodStore } from 'src/store/PaymentMethod';
import { AdyenCheckoutState } from 'src/util/adyen';
import { getConfig } from 'src/util/config';
import { getAmountWithFees } from 'src/util/getAmountWithFees';
import { currencyMap } from '../../constants/constants';
import { usePaymentsTranslation } from '../../hooks/usePaymentsTranslation';
import { AcceptCCFeeCheckbox } from '../AcceptCCFeeCheckbox/AcceptCCFeeCheckbox';
import { CheckboxRecaptcha } from '../CheckboxRecaptcha/CheckboxRecaptcha';
import { ErrorAlert, ErrorMessage } from '../ErrorAlert/ErrorAlert';
import { useHppDataStore } from '../HostedPaymentPageContainer/useHppData';
import { useHppSessionStore } from '../HostedPaymentPageContainer/useHppSession';

type Props = {
  onPaymentSuccess: (state: AdyenCheckoutState) => void;
  setErrorMessage: (errorMessage: ErrorMessage | undefined) => void;
  isDownPayment: boolean;
};

export const ExpressCheckout = ({
  onPaymentSuccess,
  setErrorMessage: _setErrorMessage,
  isDownPayment,
}: Props): React.ReactElement => {
  const { hppSession: hppSessionDetail } = useHppSessionStore();
  const { hppData, setHppData, paymentMethodConfig } = useHppDataStore();
  const { locale } = Locale.useContainer();
  const { data: agencyDetail } = useAgencyDetailsStore();
  const { t } = usePaymentsTranslation();

  // Payment total and fee calculation
  const paymentMethod = PaymentMethod.Card;
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentFee, setPaymentFee] = useState(0);

  // We have to calculate the payment fee and total upfront because there is now "selecting a payment method" step
  // Its instant checkout, so we need to know the fee and total before the user clicks the button
  useEffect(() => {
    const subtotal =
      isDownPayment && hppData.pf?.quote
        ? BigInt(hppData.pf.pfSubtotal!) + BigInt(hppData.pf.quote.retainedPayments ?? 0)
        : hppData.paymentAmount;
    if (typeof hppSessionDetail?.feeCredit === 'number' && hppData.paymentOption === TransactionType.PAID_IN_FULL) {
      const creditFee = hppSessionDetail.feeCredit;
      setPaymentFee(creditFee);
      setPaymentTotal(hppData.paymentAmount + creditFee);
    } else {
      const result = getAmountWithFees(
        paymentMethod,
        Number(subtotal),
        paymentMethodConfig[paymentMethod].fee,
        currencyMap[locale],
      );
      setPaymentFee(result.paymentFee);
      setPaymentTotal(result.paymentTotal);
    }
  }, [
    isDownPayment,
    hppData,
    hppSessionDetail,
    paymentMethodConfig,
    locale,
    setPaymentFee,
    setPaymentTotal,
    paymentMethod,
  ]);

  // Mobile wallet containers and components
  const applePayContainer = useRef<HTMLDivElement>(null);
  const adyenApplePayComponent = useRef<UIElement>();
  const googlePayContainer = useRef<HTMLDivElement>(null);
  const adyenGooglePayComponent = useRef<UIElement>();

  // Recaptcha state
  const [recaptchaAlertOpen, setRecaptchaAlertOpen] = useState(false);
  const [onRecaptchaSuccess, setOnRecaptchaSuccess] = useState<(recaptchaToken: string) => Promise<void>>();

  // Checkbox for accepting credit card fees
  const [onCCFeeAcceptance, setOnCCFeeAcceptance] = useState<(feeAccepted: boolean) => Promise<void>>();
  const [ccFeeAcceptanceAlertOpen, setCCFeeAcceptanceAlertOpen] = useState(false);
  const [isCCFeeAccepted, setIsCCFeeAccepted] = useState(false);
  const { isCcFeeCheckboxEnabled, ccFeeThresholdAmount } = agencyDetail || {};
  const showFeeCheckbox =
    isCcFeeCheckboxEnabled &&
    hppData.paymentAmount >= (ccFeeThresholdAmount ?? 0) &&
    paymentMethodConfig[PaymentMethod.Card].fee !== 0;
  const { data: paymentMethods } = usePaymentMethodStore();
  const { isApplePayEnabled, isGooglePayEnabled } = useFeatureFlags();

  const setErrorMessage = useCallback(
    (message?: ErrorMessage) => {
      _setErrorMessage(message);
    },
    [_setErrorMessage],
  );

  const { makePayment } = useMakePayment(setErrorMessage);

  const handleOnClick = useCallback(
    (resolve: any) => {
      // ZF: yes, seriously adyen typed this as 'any'
      setHppData({ paymentMethod, paymentTotal, paymentFee });

      if (showFeeCheckbox) {
        setIsCCFeeAccepted(false);
        setCCFeeAcceptanceAlertOpen(true);

        setOnCCFeeAcceptance(() => async (feeAccepted: boolean) => {
          setIsCCFeeAccepted(feeAccepted);
          setTimeout(() => {
            setCCFeeAcceptanceAlertOpen(false);
            resolve();
          }, 400); // Delay to show checkbox state change before resolving
        });
      } else resolve();
    },
    [
      setHppData,
      showFeeCheckbox,
      setCCFeeAcceptanceAlertOpen,
      setIsCCFeeAccepted,
      paymentTotal,
      paymentFee,
      paymentMethod,
    ],
  );

  const handleOnSubmit = useCallback(
    (state: AdyenCheckoutState, component: UIElement, { resolve }: SubmitActions) => {
      if (!state.isValid) {
        component.showValidation();
        console.error('Form is invalid', state);
        return;
      }

      const _hppSessionDetail = hppSessionDetail;

      setOnRecaptchaSuccess(() => async (recaptchaToken: string) => {
        setRecaptchaAlertOpen(false);
        await makePayment({
          onSuccessCallback: onPaymentSuccess,
          paymentMethod: state.data.paymentMethod,
          recaptchaToken: recaptchaToken,
          state: state,
          hppData: hppData,
          storePaymentMethod: state.data.storePaymentMethod,
          hppSessionDetail: _hppSessionDetail,
          paymentTotal: hppData.paymentTotal,
          convenienceFee: hppData.paymentFee,
        });
      });
      // Calling resolve here so the googlepay/applepay modal closes. We then proceed with the recaptcha flow to complete the payment.
      // Don't think the resultCode matters that much as long as its not 'Error' or 'Refused'.
      resolve({ resultCode: 'ChallengeShopper' });
      setRecaptchaAlertOpen(true);
    },
    [hppData, hppSessionDetail, makePayment, onPaymentSuccess],
  );

  const applePayConfig =
    paymentMethods &&
    paymentMethods.find((paymentMethod) => paymentMethod.type === AdyenPaymentMethod.ApplePay)?.configuration;

  const showApplePay = useMemo(() => {
    return (
      isApplePayEnabled &&
      agencyDetail?.applePayEnabled &&
      hppSessionDetail?.allowedPaymentMethods.includes(PaymentMethod.ApplePay) &&
      applePayConfig
    );
  }, [isApplePayEnabled, agencyDetail, hppSessionDetail, applePayConfig]);

  const showGooglePay = useMemo(() => {
    return (
      isGooglePayEnabled &&
      agencyDetail?.googlePayEnabled &&
      hppSessionDetail?.allowedPaymentMethods.includes(PaymentMethod.GooglePay)
    );
  }, [isGooglePayEnabled, agencyDetail, hppSessionDetail]);

  const { checkout, errorMessage: adyenErrorMessage } = useAdyenConfig({
    locale,
    total: paymentTotal,
    handleOnSubmit,
    showPayButton: true,
    applePayConfig: applePayConfig,
  });

  const googlePayTokenMissing = getConfig('REACT_APP_TIER') === 'production' && !hppSessionDetail?.googlePayToken;
  useEffect(() => {
    if (showGooglePay && googlePayContainer.current && checkout && googlePayTokenMissing) {
      console.error('Google Pay token is missing, please check your HPP session configuration.');
    }
  }, [googlePayTokenMissing, showGooglePay, googlePayContainer, checkout]);

  useEffect(() => {
    let isMounted = true;
    const createCheckout = () => {
      try {
        if (isMounted && showApplePay && applePayContainer.current && checkout) {
          if (adyenApplePayComponent.current) {
            checkout.remove(adyenApplePayComponent.current);
          }

          adyenApplePayComponent.current = new ApplePay(checkout, {
            buttonType: 'plain',
            buttonColor: 'black',
            onClick: handleOnClick,
          }).mount(applePayContainer.current);
        }

        if (isMounted && showGooglePay && googlePayContainer.current && checkout && !googlePayTokenMissing) {
          if (adyenGooglePayComponent.current) {
            checkout.remove(adyenGooglePayComponent.current);
          }

          adyenGooglePayComponent.current = new GooglePay(checkout, {
            buttonType: 'plain',
            buttonColor: 'black',
            onClick: handleOnClick,
          }).mount(googlePayContainer.current);
        }
        setErrorMessage(undefined);
      } catch (err: unknown) {
        console.error('failed to create+mount Adyen checkout express checkout components', err);
        setErrorMessage(['INIT_ADYEN_CHECKOUT_FAILED']);
      }
    };
    createCheckout();
    return () => {
      isMounted = false;
    };
  }, [checkout, setErrorMessage, locale, showApplePay, showGooglePay, handleOnClick, googlePayTokenMissing]);

  return (
    <>
      <H4 className="mb-0">{t('EXPRESS_CHECKOUT')}</H4>
      <Flex>
        <Row>
          {showApplePay && (
            <Col xs={12} sm={6}>
              <div ref={applePayContainer}></div>
            </Col>
          )}
          {showGooglePay && (
            <Col xs={12} sm={6}>
              <div ref={googlePayContainer}></div>
            </Col>
          )}
        </Row>
      </Flex>
      {!!adyenErrorMessage && <ErrorAlert errorMessage={adyenErrorMessage} />}
      {showFeeCheckbox && (
        <Alert open={ccFeeAcceptanceAlertOpen} customDataProps={{ 'data-css': 'alert-hide-close' }}>
          <ModalBody>
            <AcceptCCFeeCheckbox onCheckboxChange={onCCFeeAcceptance!} isChecked={isCCFeeAccepted} />
          </ModalBody>
        </Alert>
      )}
      <CheckboxRecaptcha
        onError={() => {
          setErrorMessage(['RECAPTCHA_BROWSER_ERROR_WITH_ID']);
          setRecaptchaAlertOpen(false);
        }}
        onSuccess={onRecaptchaSuccess!}
        recaptchaAlertOpen={recaptchaAlertOpen}
        action="purchase"
      />
    </>
  );
};
